import {
  Box,
  Skeleton,
  Alert,
  AlertIcon,
  AlertDescription,
  useToast,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/browser';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  utils as commonUtils,
  error as commonError,
} from '@finley-technologies/common';
import { Explo } from '@finley-technologies/finley-types';

import { useGenerateExploApiJwtQuery } from '../requests/explo.gql.generated';
import { generateMissingIntegrationMessage } from '../utils';

type ExploReportBuilderContainerProps = {
  organizationId: string;
  creditFacilityId?: string;
  exploPlacement: Explo.ExploPlacement.AllOptionsType;
};
export const ExploReportBuilderContainer = ({
  creditFacilityId,
  organizationId,
  exploPlacement,
}: ExploReportBuilderContainerProps) => {
  const toast = useToast();
  const navigate = useNavigate();

  const { data: tokenData } = useGenerateExploApiJwtQuery({
    fetchPolicy: 'network-only',
    variables: {
      organizationId,
      creditFacilityId,
      exploArtifactType: 'reportBuilder',
      exploPlacement,
    },
    onError: (error) => {
      Sentry.captureException(
        new commonError.FinleyError(
          `Failed to generate Explo API JWT for Report Builder [error:${error.message}]`,
          {
            metadata: {
              logMeta: {
                error,
                organizationId,
                creditFacilityId,
                exploPlacement,
              },
            },
          },
        ),
      );

      toast({
        title:
          'An error occurred while attempting to view report builder. Please contact support@finleycms.com if this continues.',
        status: 'error',
        position: 'top',
        duration: 6000,
        isClosable: true,
      });
      navigate('/');
    },
  });

  if (!tokenData) {
    return <Skeleton />;
  }

  if (!tokenData.tokenResponse.isIntegrationAvailable) {
    return (
      <Alert status="info">
        <AlertIcon />
        <AlertDescription>
          {generateMissingIntegrationMessage({
            exploArtifactType: 'reportBuilder',
            exploPlacement,
          })}
        </AlertDescription>
      </Alert>
    );
  }

  if (tokenData.tokenResponse.error) {
    Sentry.captureException(
      new commonError.FinleyError(
        `Failed to fetch Explo JWT [type:reportBuilder] [placement:${exploPlacement}] [error:${tokenData.tokenResponse.error}]`,
        { metadata: { logMeta: { organizationId, creditFacilityId } } },
      ),
    );
    return (
      <Alert status="warning">
        <AlertIcon />
        <AlertDescription>
          An error occurred when fetching Strats Report Builder. Please contact
          Finley support for assistance if this continues.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Box id="explo-report-builder-container" h="800px">
      <explo-report-builder
        jwt={tokenData.tokenResponse.jwt}
        environment={commonUtils.EnvUtils.getEnv()}
      />
    </Box>
  );
};
